import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ApprovalInstance, ApprovalStepInstance, ResolvedApprovalStep } from '../models/approval-flow.model';
import { ApprovalService } from '../services/approval.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { UserService } from '../services/user.service';
import { FormSignatureComponent } from '../../../items/form-signature.component';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { I18nService } from '../../../../services/i18n.service';
import { EmailService } from '../../../../services/email.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../services/global-environment';

// 定義表單欄位配置介面
export interface FormFieldConfig {
  type: string;
  label: string;
  id: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
}

// Add interface extension for action result
interface ApprovalStepInstanceWithAction extends ApprovalStepInstance {
  actionResult?: {
    success: boolean;
    message: string;
  } | null;
}

@Component({
  selector: 'app-approval-status',
  templateUrl: './approval-status.component.html',
  standalone: true,
  styleUrls: ['./approval-status.component.scss'],
  imports: [CommonModule, FormsModule, FormSignatureComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApprovalStatusComponent implements OnInit, OnChanges {
  @Input() formId: string = "";
  @Input() approvalFlow: ResolvedApprovalStep[] = []; // 新增：簽核流程資料
  @Input() stepIndex: number = -1; // 新增：指定要顯示的階段索引，-1表示顯示所有階段
  @Input() approvalResult: any = null; // 新增：簽核結果資料，包含歷史資料
  @Output() approvalComplete = new EventEmitter<ApprovalInstance>();

  @Input() showCommentField: boolean = true;  // 是否顯示意見欄位
  @Input() showSignatureField: boolean = true; // 是否顯示簽名欄位
  @Input() signatureRequired: boolean = false;

  approvalInstance: ApprovalInstance | null = null;
  loading = false;
  error = false;
  comment = '';
  signature: string | null = null;
  actionInProgress = false;
  currentUserId: string = '';
  currentUserName: string = ''; // 新增：目前使用者名稱
  currentUserInfo: any = null; // 新增：目前使用者完整資訊

  // 新增：自訂表單相關屬性
  customFormValidation: { [stepIndex: number]: boolean } = {}; // 每個步驟的表單驗證狀態
  customFormData: { [stepIndex: number]: any } = {}; // 每個步驟的表單資料

  // 新增：測試模式開關（用於放寬權限檢查）
  isTestMode: boolean = true; // 設為 true 以進行測試

  constructor(
    private approvalService: ApprovalService,
    private userService: UserService,
    private i18nService: I18nService,
    private emailService: EmailService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.getCurrentUserId();
    this.getCurrentUserInfo();
    this.loadApprovalStatusIfNeeded();

    // 測試模式下，如果沒有 approvalFlow 資料，創建示例資料
    if (this.isTestMode && this.approvalFlow.length === 0) {
      this.createTestApprovalFlow();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formId']) {
      this.loadApprovalStatusIfNeeded();
    }
    if (changes['approvalFlow'] && this.approvalFlow.length > 0) {
      this.createApprovalInstanceFromFlow();
    }
    if (changes['stepIndex'] || changes['approvalResult']) {
      // 當stepIndex或approvalResult變更時，重新處理顯示邏輯
      this.cdr.markForCheck();
    }
  }

  getCurrentUserId() {
    this.currentUserId = this.userService.getCurrentUserId();
  }

  getCurrentUserInfo() {
    // 取得目前使用者的詳細資訊
    this.userService.getCurrentUserDetails().subscribe({
      next: (userInfo: any) => {
        this.currentUserInfo = userInfo;
        this.currentUserName = userInfo?.username || userInfo?.name || this.currentUserId;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.warn('無法取得使用者資訊:', error);
        this.currentUserName = this.currentUserId;
        this.cdr.markForCheck();
      }
    });
  }

  loadApprovalStatusIfNeeded() {
    if (this.approvalFlow.length > 0) {
      // 如果有傳入簽核流程資料，使用該資料建立簽核實例
      this.createApprovalInstanceFromFlow();
    } else if (this.formId) {
      this.loadApprovalStatus();
    } else {
      this.formId = 'test_form_1';
      this.loadApprovalStatus();
    }
  }

  loadApprovalStatus() {
    this.loading = true;
    this.error = false;

    this.approvalService.getApprovalForForm(this.formId)
      .pipe(
        catchError(error => {
          console.error('Error loading approval status', error);
          this.error = true;
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(instance => {
        if (instance) {
          this.approvalInstance = instance;
        }
        this.cdr.markForCheck();
      });
  }

  isCurrentApprover(stepIndex: number): boolean {
    if (!this.approvalInstance) return false;

    const actualIndex = this.getActualStepIndex(stepIndex);

    // 測試模式：允許當前用戶簽核所有步驟
    if (this.isTestMode) {
      // 單一階段模式時，檢查該階段是否為進行中狀態
      if (this.stepIndex >= 0) {
        const stepStatus = this.getStepDisplayStatus(stepIndex);
        return stepStatus === 'in_progress' || stepStatus === 'pending';
      }
      // 一般模式時，檢查是否為當前步驟
      return actualIndex === this.approvalInstance.currentStepIndex;
    }

    // 單一階段模式時，不需要檢查是否為當前步驟，只需檢查狀態和權限
    if (this.stepIndex >= 0) {
      const stepStatus = this.getStepDisplayStatus(stepIndex);
      if (stepStatus !== 'in_progress' && stepStatus !== 'pending') {
        return false; // 已完成的階段不可操作
      }
    } else {
      // 一般模式時，檢查當前步驟是否為進行中狀態
      if (actualIndex !== this.approvalInstance.currentStepIndex) return false;
    }

    // 如果使用新的簽核流程資料結構，檢查目前使用者是否在可簽核人員列表中
    if (this.approvalFlow.length > 0) {
      const isInList = this.isCurrentUserInApproverList(actualIndex);
      return isInList;
    }

    // 原本的邏輯：檢查當前步驟和使用者ID
    const currentStep = this.approvalInstance.steps[actualIndex];
    const isOriginalApprover = currentStep.approver.userId === this.currentUserId;
    return isOriginalApprover;
  }

  // 處理簽名變更
  onSignatureChange(value: string | null): void {
    this.signature = value;
  }

  canPerformAction(): boolean {
    if (this.signatureRequired && !this.signature) {
      return false;
    }
    return true;
  }

  approve(stepIndex: number) {
    // 檢查簽名要求
    if (this.signatureRequired && !this.signature) {
      const currentStep = this.approvalInstance!.steps[stepIndex] as ApprovalStepInstanceWithAction;
      currentStep.actionResult = {
        success: false,
        message: this.i18nService.translate('approval.status.required_fields_error')
      };
      return;
    }

    // 暫時移除自訂表單驗證阻擋，讓功能更穩定
    // 未來可以根據需要重新啟用
    /*
    if (!this.isCustomFormValid(stepIndex)) {
      const currentStep = this.approvalInstance!.steps[stepIndex] as ApprovalStepInstanceWithAction;
      currentStep.actionResult = {
        success: false,
        message: '請完成所有必填的自訂表單欄位'
      };
      return;
    }
    */

    // 執行簽核動作
    this.performAction('approve', stepIndex);

    // 簽核完成後，檢查下一關並發送通知郵件
    this.sendNotificationToNextApprovers(stepIndex);
  }

  reject(stepIndex: number) {
    // 檢查簽名要求
    if (this.signatureRequired && !this.signature) {
      const currentStep = this.approvalInstance!.steps[stepIndex] as ApprovalStepInstanceWithAction;
      currentStep.actionResult = {
        success: false,
        message: this.i18nService.translate('approval.status.required_fields_error')
      };
      return;
    }

    // 拒絕操作不需要檢查自訂表單驗證，但仍需記錄資料
    this.performAction('reject', stepIndex);
  }

  performAction(action: 'approve' | 'reject', stepIndex: number) {
    if (!this.approvalInstance) return;

    const currentStep = this.approvalInstance.steps[stepIndex] as ApprovalStepInstanceWithAction;

    this.actionInProgress = true;
    // 重置該步驟的操作結果
    currentStep.actionResult = null;

    // 在 Mock 資料狀態下，直接模擬簽核完成
    if (this.approvalFlow.length > 0) {
      this.simulateApprovalAction(action, stepIndex);
      return;
    }

    // 原本的 API 呼叫邏輯
    this.approvalService.performApprovalAction(
      this.approvalInstance.id,
      action,
      this.comment,
      this.signature
    ).pipe(
      catchError(error => {
        console.error('Error performing approval action', error);
        // 設置當前步驟的操作結果
        currentStep.actionResult = {
          success: false,
          message: this.i18nService.translate('approval.status.action_failed')
        };
        return of(null);
      }),
      finalize(() => {
        this.actionInProgress = false;
        this.cdr.markForCheck();
      })
    ).subscribe(updatedInstance => {
      if (updatedInstance) {
        this.approvalInstance = updatedInstance;

        // 更新當前步驟的操作結果
        const updatedStep = this.approvalInstance.steps[stepIndex] as ApprovalStepInstanceWithAction;
        const actionText = action === 'approve' ? this.i18nService.translate('approval.status.approve') : this.i18nService.translate('approval.status.reject');
        updatedStep.actionResult = {
          success: true,
          message: this.i18nService.translate('approval.status.action_completed', { action: actionText })
        };

        // 清空表單數據
        this.comment = '';
        this.signature = null;

        // 通知父組件
        this.approvalComplete.emit(updatedInstance);

        // 觸發變更檢測
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * 模擬簽核操作（用於 Mock 資料狀態）
   */
  simulateApprovalAction(action: 'approve' | 'reject', stepIndex: number) {
    if (!this.approvalInstance) return;

    const currentStep = this.approvalInstance.steps[stepIndex] as ApprovalStepInstanceWithAction;

    // 模擬處理延遲
    setTimeout(() => {
      // 更新當前步驟狀態
      currentStep.status = action === 'approve' ? 'approved' : 'rejected';
      currentStep.comment = this.comment;
      currentStep.signature = this.signature;
      currentStep.actionDate = new Date();

      // 保存自訂表單資料
      if (this.customFormData[stepIndex]) {
        (currentStep as any).customFormData = { ...this.customFormData[stepIndex] };
      }

      // 設置操作結果
      const actionText = action === 'approve' ? this.i18nService.translate('approval.status.approve') : this.i18nService.translate('approval.status.reject');
      currentStep.actionResult = {
        success: true,
        message: this.i18nService.translate('approval.status.action_completed', { action: actionText })
      };

      // 如果同意且還有下一步驟，進入下一步驟
      if (action === 'approve' && stepIndex < this.approvalInstance!.steps.length - 1) {
        this.approvalInstance!.currentStepIndex = stepIndex + 1;
        this.approvalInstance!.steps[stepIndex + 1].status = 'in_progress';
      } else {
        // 如果是最後一步或被拒絕，結束流程
        this.approvalInstance!.status = action === 'approve' ? 'approved' : 'rejected';
        this.approvalInstance!.completedAt = new Date();
      }

      this.approvalInstance!.updatedAt = new Date();

      // 清空表單數據
      this.comment = '';
      this.signature = null;
      this.actionInProgress = false;

      // 通知父組件
      this.approvalComplete.emit(this.approvalInstance!);

      // 觸發變更檢測
      this.cdr.markForCheck();
    }, 500); // 500ms 延遲模擬網路請求
  }

  // 新增方法：檢查步驟是否已完成操作
  isStepActionCompleted(stepIndex: number): boolean {
    if (!this.approvalInstance) {
      return false;
    }

    // 單一階段模式時，檢查該階段的狀態
    if (this.stepIndex >= 0) {
      const stepStatus = this.getStepDisplayStatus(0); // 在單一階段模式中，displayIndex 總是 0
      return stepStatus === 'approved' || stepStatus === 'rejected';
    }

    // 一般模式時，檢查 actionResult
    const step = this.approvalInstance.steps[stepIndex] as ApprovalStepInstanceWithAction;
    const isCompleted = step.actionResult ? step.actionResult.success : false;

    return isCompleted;
  }

  /**
   * 取得要顯示的步驟列表（支援單一階段顯示）
   */
  getDisplaySteps(): ApprovalStepInstance[] {
    if (!this.approvalInstance) return [];

    // 如果指定了特定stepIndex，只顯示該階段
    if (this.stepIndex >= 0 && this.stepIndex < this.approvalInstance.steps.length) {
      return [this.approvalInstance.steps[this.stepIndex]];
    }

    // 否則顯示所有階段
    return this.approvalInstance.steps;
  }

  /**
   * 取得步驟的實際索引（用於單一階段顯示時的索引轉換）
   */
  getActualStepIndex(displayIndex: number): number {
    return this.stepIndex >= 0 ? this.stepIndex : displayIndex;
  }

  /**
   * 檢查階段是否為只讀模式（已完成或非當前階段）
   */
  isStepReadOnly(stepIndex: number): boolean {
    if (!this.approvalInstance) return true;

    const actualIndex = this.getActualStepIndex(stepIndex);

    // 單一階段模式時，檢查該階段的狀態
    if (this.stepIndex >= 0) {
      const stepStatus = this.getStepDisplayStatus(stepIndex);
      return stepStatus === 'approved' || stepStatus === 'rejected';
    }

    const step = this.approvalInstance.steps[actualIndex];

    // 如果有簽核結果資料，優先使用
    if (this.approvalResult && this.approvalResult.stepResults) {
      const stepResult = this.getStepResultFromApprovalResult(actualIndex);
      return stepResult?.completed === true;
    }

    // 已完成的階段（approved或rejected）為只讀
    return step.status === 'approved' || step.status === 'rejected';
  }

  /**
   * 從approvalResult中取得指定步驟的結果
   */
  getStepResultFromApprovalResult(stepIndex: number): any {
    return this.approvalResult?.stepResults?.find(
      (result: any) => result.stepIndex === stepIndex
    );
  }

  /**
   * 取得階段的歷史資料（意見、簽名、自訂表單資料等）
   */
  getStepHistoryData(stepIndex: number): any {
    if (!this.approvalInstance) return null;

    const actualIndex = this.getActualStepIndex(stepIndex);

    // 當顯示單一階段時，優先從 approvalFlow 中取得狀態資料
    if (this.stepIndex >= 0 && this.approvalFlow && this.approvalFlow[actualIndex]) {
      const flowStep = this.approvalFlow[actualIndex] as any;
      if (flowStep.status && (flowStep.status === 'approved' || flowStep.status === 'rejected')) {
        return {
          comment: flowStep.comment || '',
          signature: flowStep.signature || null,
          customFormData: this.extractCustomFormDataFromFlow(flowStep),
          actionDate: flowStep.actionDate || null,
          status: flowStep.status
        };
      }
    }

    // 優先從approvalResult中取得歷史資料
    if (this.approvalResult && this.approvalResult.stepResults) {
      const stepResult = this.getStepResultFromApprovalResult(actualIndex);
      if (stepResult && stepResult.completed) {
        return {
          comment: stepResult.comment || '',
          signature: stepResult.signature || null,
          customFormData: stepResult.customFormData || {},
          actionDate: stepResult.actionDate || null,
          status: stepResult.action === 'approve' ? 'approved' : 'rejected'
        };
      }
    }

    // 如果沒有approvalResult，從step資料中取得
    const step = this.approvalInstance.steps[actualIndex];
    return {
      comment: step.comment || '',
      signature: step.signature || null,
      customFormData: (step as any).customFormData || {},
      actionDate: step.actionDate || null,
      status: step.status
    };
  }

  /**
   * 取得階段的顯示狀態（考慮歷史資料）
   */
  getStepDisplayStatus(stepIndex: number): string {
    const actualIndex = this.getActualStepIndex(stepIndex);

    // 當顯示單一階段時，優先使用 approvalFlow 中的狀態
    if (this.stepIndex >= 0 && this.approvalFlow && this.approvalFlow[actualIndex]) {
      const flowStep = this.approvalFlow[actualIndex] as any;
      if (flowStep.status) {
        return flowStep.status;
      }
    }

    const historyData = this.getStepHistoryData(stepIndex);
    if (historyData && historyData.status) {
      return historyData.status;
    }

    return this.approvalInstance?.steps[actualIndex]?.status || 'pending';
  }
  getStepActionResult(stepIndex: number): { success: boolean, message: string } | null {
    if (!this.approvalInstance || !this.approvalInstance.steps[stepIndex]) return null;

    const step = this.approvalInstance.steps[stepIndex] as ApprovalStepInstanceWithAction;
    return step.actionResult || null;
  }

  /**
   * 從簽核流程資料建立簽核實例
   */
  createApprovalInstanceFromFlow() {
    if (!this.approvalFlow || this.approvalFlow.length === 0) return;

    const steps: ApprovalStepInstance[] = this.approvalFlow.map((flowStep: any, index) => {
      // 優先使用 approvalFlow 中的狀態資料
      let stepStatus: 'pending' | 'in_progress' | 'approved' | 'rejected' = 'pending';
      let stepComment = '';
      let stepSignature: string | null = null;
      let stepActionDate: Date | null = null;
      let stepCustomFormData: any = {};

      // 首先檢查 approvalFlow 中是否有狀態資料
      if (flowStep.status) {
        stepStatus = flowStep.status;
        stepComment = flowStep.comment || '';
        stepSignature = flowStep.signature || null;
        stepActionDate = flowStep.actionDate ? new Date(flowStep.actionDate) : null;

        // 從 customFormItems 中提取表單資料
        if (flowStep.customFormItems) {
          stepCustomFormData = {};
          flowStep.customFormItems.forEach((item: any) => {
            if (item.value !== undefined) {
              stepCustomFormData[item.name] = item.value;
            }
          });
        }
      } else if (this.approvalResult && this.approvalResult.stepResults) {
        // 如果 approvalFlow 沒有狀態，才檢查 approvalResult
        const stepResult = this.getStepResultFromApprovalResult(index);
        if (stepResult && stepResult.completed) {
          stepStatus = stepResult.action === 'approve' ? 'approved' : 'rejected';
          stepComment = stepResult.comment || '';
          stepSignature = stepResult.signature || null;
          stepActionDate = stepResult.actionDate ? new Date(stepResult.actionDate) : null;
          stepCustomFormData = stepResult.customFormData || {};
        } else if (index === (this.approvalResult.currentStep || 0)) {
          stepStatus = 'in_progress';
        }
      } else {
        // 沒有任何狀態資料時，使用預設邏輯
        stepStatus = index === 0 ? 'in_progress' : 'pending';
      }

      return {
        stepDefinitionId: `step_${flowStep.level}`,
        name: flowStep.stepName,
        order: flowStep.level,
        approver: {
          userId: flowStep.userId || '', // 可能是空的，因為可能是角色基礎的簽核
          userName: flowStep.userName || this.getStepDepartmentRoleInfo(index),
          roleId: flowStep.roleId || '',
          roleName: flowStep.roleName || '',
          departmentId: flowStep.departmentId || '',
          departmentName: flowStep.departmentName || ''
        },
        status: stepStatus,
        comment: stepComment,
        signature: stepSignature,
        actionDate: stepActionDate,
        customFormData: stepCustomFormData
      } as ApprovalStepInstance & { customFormData: any };
    });

    const currentStepIndex = this.approvalResult?.currentStep || 0;
    let overallStatus: 'in_progress' | 'approved' | 'rejected' = 'in_progress';

    // 判斷整體狀態
    if (this.approvalResult) {
      const lastStepResult = this.approvalResult.stepResults?.find(
        (result: any) => result.stepIndex === steps.length - 1
      );
      if (lastStepResult && lastStepResult.completed) {
        overallStatus = lastStepResult.action === 'approve' ? 'approved' : 'rejected';
      } else if (this.approvalResult.stepResults?.some((result: any) =>
        result.completed && result.action === 'reject'
      )) {
        overallStatus = 'rejected';
      }
    }

    this.approvalInstance = {
      id: `approval_${this.formId}_${Date.now()}`,
      flowDefinitionId: 'custom_flow',
      formId: this.formId,
      formTypeId: 'custom_form',
      initiator: {
        userId: this.currentUserId,
        userName: this.currentUserName || 'Current User',
        departmentId: '',
        departmentName: ''
      },
      status: overallStatus,
      currentStepIndex: currentStepIndex,
      steps: steps,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // 觸發變更檢測
    this.cdr.markForCheck();
  }

  get statusText(): string {
    if (!this.approvalInstance) return this.i18nService.translate('approval.status.not_started');

    switch (this.approvalInstance.status) {
      case 'in_progress': return this.i18nService.translate('approval.status.in_progress');
      case 'approved': return this.i18nService.translate('approval.status.approved');
      case 'rejected': return this.i18nService.translate('approval.status.rejected');
      default: return this.i18nService.translate('approval.status.unknown');
    }
  }

  get statusClass(): string {
    if (!this.approvalInstance) return 'status-pending';

    switch (this.approvalInstance.status) {
      case 'in_progress': return 'status-in-progress';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      default: return 'status-pending';
    }
  }

  getStepClass(status: string): string {
    return `step-${status}`;
  }

  getStatusClass(status: string): string {
    return `status-${status}`;
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'pending': return this.i18nService.translate('approval.status.step.pending');
      case 'in_progress': return this.i18nService.translate('approval.status.step.in_progress');
      case 'approved': return this.i18nService.translate('approval.status.step.approved');
      case 'rejected': return this.i18nService.translate('approval.status.step.rejected');
      default: return this.i18nService.translate('approval.status.step.unknown');
    }
  }

  /**
   * 取得指定步驟的可簽核人員列表
   */
  getStepApproverList(stepIndex: number): any[] {
    if (!this.approvalFlow || !this.approvalFlow[stepIndex]) {
      return [];
    }

    const step = this.approvalFlow[stepIndex];
    return (step as any).approverList || [];
  }

  /**
   * 檢查目前使用者是否在可簽核人員列表中
   */
  isCurrentUserInApproverList(stepIndex: number): boolean {
    const approverList = this.getStepApproverList(stepIndex);
    return approverList.some(approver => approver.id === this.currentUserId);
  }

  /**
   * 取得步驟的部門角色資訊
   */
  getStepDepartmentRoleInfo(stepIndex: number): string {
    if (!this.approvalFlow || !this.approvalFlow[stepIndex]) {
      return '';
    }

    const step = this.approvalFlow[stepIndex] as any;
    if (step.approverType === 'role_based') {
      const deptName = step.departmentName || '未知部門';
      const roleName = step.roleName || '未知角色';
      return `${deptName} - ${roleName}`;
    } else if (step.approverType === 'specific_user') {
      return '指定人員';
    }
    return '';
  }

  // 新增：自訂表單相關方法

  /**
   * 檢查步驟是否有自訂表單
   */
  hasCustomForm(stepIndex: number): boolean {
    // 檢查 approvalFlow 資料結構
    if (!this.approvalFlow || !this.approvalFlow[stepIndex]) {
      return false;
    }

    const step = this.approvalFlow[stepIndex] as any;
    const hasCustomForm = step.customFormItems && step.customFormItems.length > 0;

    return hasCustomForm;
  }

  /**
   * 取得步驟的自訂表單設定
   */
  getStepCustomFormFields(stepIndex: number): FormFieldConfig[] {
    if (!this.approvalFlow || !this.approvalFlow[stepIndex]) {
      return [];
    }

    const step = this.approvalFlow[stepIndex] as any;
    const customFormItems = step.customFormItems || [];

    return customFormItems;
  }

  /**
   * 取得自訂表單欄位的值
   */
  getCustomFormFieldValue(stepIndex: number, fieldName: string): any {
    if (!this.customFormData[stepIndex]) {
      this.customFormData[stepIndex] = {};
    }
    return this.customFormData[stepIndex][fieldName] || '';
  }

  /**
   * 更新自訂表單欄位的值
   */
  updateCustomFormFieldValue(stepIndex: number, fieldName: string, value: any): void {
    if (!this.customFormData[stepIndex]) {
      this.customFormData[stepIndex] = {};
    }
    this.customFormData[stepIndex][fieldName] = value;

    // 觸發原有的變更事件處理
    this.onCustomFormDataChange(stepIndex, this.customFormData[stepIndex]);
  }

  /**
   * 自訂表單資料變更處理
   */
  onCustomFormDataChange(stepIndex: number, formData: any): void {
    this.customFormData[stepIndex] = { ...formData };

    // 更新表單驗證狀態（這裡可以加入更複雜的驗證邏輯）
    this.customFormValidation[stepIndex] = this.validateCustomFormData(stepIndex, formData);
  }

  /**
   * 驗證自訂表單資料（可擴展）
   */
  private validateCustomFormData(stepIndex: number, formData: any): boolean {
    // 測試模式：總是回傳 true
    if (this.isTestMode) {
      return true;
    }

    const formFields = this.getStepCustomFormFields(stepIndex);

    // 檢查必填欄位
    for (const field of formFields) {
      if (field.required && (!formData[field.name] || formData[field.name].trim() === '')) {
        return false;
      }
    }

    return true;
  }

  /**
   * 檢查自訂表單是否有效（用於控制簽核按鈕）
   */
  isCustomFormValid(stepIndex: number): boolean {
    // 測試模式：總是回傳 true，讓使用者可以正常簽核
    if (this.isTestMode) {
      return true;
    }

    // 如果沒有自訂表單，視為有效
    if (!this.hasCustomForm(stepIndex)) {
      return true;
    }

    // 檢查表單驗證狀態
    const isValid = this.customFormValidation[stepIndex] ?? true;
    return isValid;
  }

  /**
   * 檢查是否有自訂表單資料
   */
  hasCustomFormData(customFormData: any): boolean {
    if (!customFormData || typeof customFormData !== 'object') {
      return false;
    }

    return Object.keys(customFormData).some(key =>
      customFormData[key] !== null &&
      customFormData[key] !== undefined &&
      customFormData[key] !== ''
    );
  }

  /**
   * TrackBy function for step iteration to optimize ngFor performance
   */
  trackByStepIndex(index: number, item: any): number {
    return index;
  }

  /**
   * TrackBy function for form field iteration to optimize ngFor performance
   */
  trackByFieldName(index: number, field: FormFieldConfig): string {
    return field.name || field.id || index.toString();
  }

  /**
   * TrackBy function for approver list iteration
   */
  trackByApproverId(index: number, approver: any): string {
    return approver.id || approver.name || index.toString();
  }

  /**
   * TrackBy function for select options
   */
  trackByOptionValue(index: number, option: any): string {
    return option.value || index.toString();
  }

  /**
   * 創建測試用的簽核流程資料（包含自訂表單）
   * 僅在測試模式下使用
   */
  private createTestApprovalFlow(): void {
    if (!this.isTestMode) return;

    this.approvalFlow = [
      {
        stepName: '品質檢驗',
        level: 1,
        approverType: 'role_based',
        departmentName: '品質部',
        roleName: '品質檢驗員',
        userId: this.currentUserId, // 設為當前用戶，確保可以簽核
        userName: this.currentUserName,
        // 新增自訂表單範例
        customFormItems: [
          {
            type: 'textarea',
            label: '檢驗意見',
            id: 'inspection_comment',
            name: 'inspection_comment',
            placeholder: '請填寫檢驗意見...',
            required: true
          },
          {
            type: 'text-input',
            label: '檢驗編號',
            id: 'inspection_number',
            name: 'inspection_number',
            placeholder: '請輸入檢驗編號',
            required: false
          },
          {
            type: 'select',
            label: '檢驗結果',
            id: 'inspection_result',
            name: 'inspection_result',
            options: [
              { value: 'pass', label: '合格' },
              { value: 'fail', label: '不合格' },
              { value: 'conditional', label: '有條件通過' }
            ],
            required: true
          }
        ]
      },
      {
        stepName: '主管審核',
        level: 2,
        approverType: 'role_based',
        departmentName: '管理部',
        roleName: '部門主管',
        userId: this.currentUserId, // 設為當前用戶，確保可以簽核
        userName: this.currentUserName,
        // 第二步驟的自訂表單
        customFormItems: [
          {
            type: 'textarea',
            label: '主管意見',
            id: 'manager_comment',
            name: 'manager_comment',
            placeholder: '請填寫審核意見...',
            required: true
          },
          {
            type: 'date',
            label: '預計完成日期',
            id: 'expected_date',
            name: 'expected_date',
            required: false
          }
        ]
      }
    ] as any[];

    // 創建對應的簽核實例
    this.createApprovalInstanceFromFlow();
  }

  /**
   * 從簽核流程步驟中提取自訂表單資料
   */
  private extractCustomFormDataFromFlow(flowStep: any): any {
    const customFormData: any = {};

    if (flowStep.customFormItems && Array.isArray(flowStep.customFormItems)) {
      flowStep.customFormItems.forEach((item: any) => {
        if (item.name && item.value !== undefined) {
          customFormData[item.name] = item.value;
        }
      });
    }

    return customFormData;
  }

  /**
   * 調試用：檢查簽核操作區域顯示條件
   */
  debugApprovalAreaConditions(stepIndex: number): void {
    const actualIndex = this.getActualStepIndex(stepIndex);
    const isReadOnly = this.isStepReadOnly(stepIndex);
    const isApprover = this.isCurrentApprover(actualIndex);
    const isCompleted = this.isStepActionCompleted(actualIndex);
    const stepStatus = this.getStepDisplayStatus(stepIndex);

    console.log('簽核操作區域顯示條件檢查:', {
      stepIndex,
      actualIndex,
      stepStatus,
      isReadOnly,
      isApprover,
      isCompleted,
      shouldShow: !isReadOnly && isApprover && !isCompleted,
      currentUserId: this.currentUserId,
      stepMode: this.stepIndex >= 0 ? 'single' : 'all'
    });
  }

  /**
   * 簽核同意後發送通知給下一關簽核人員
   * 只在 approve() 方法中呼叫，reject() 方法不呼叫
   */
  private async sendNotificationToNextApprovers(currentStepIndex: number): Promise<void> {
    try {
      // 計算下一關的索引
      const actualCurrentIndex = this.getActualStepIndex(currentStepIndex);
      const nextStepIndex = actualCurrentIndex + 1;

      console.log('檢查是否需要發送簽核通知:', {
        currentStepIndex,
        actualCurrentIndex,
        nextStepIndex,
        totalSteps: this.approvalFlow?.length || 0
      });

      // 檢查是否有下一關
      if (!this.approvalFlow || nextStepIndex >= this.approvalFlow.length) {
        console.log('沒有下一關簽核步驟，不發送通知');
        return;
      }

      const nextStep = this.approvalFlow[nextStepIndex];

      // 檢查下一關是否有 approverList
      if (!nextStep || !(nextStep as any).approverList || (nextStep as any).approverList.length === 0) {
        console.log('下一關沒有 approverList 或為空，不發送通知', {
          hasNextStep: !!nextStep,
          hasApproverList: !!(nextStep as any).approverList,
          approverListLength: (nextStep as any).approverList?.length || 0
        });
        return;
      }

      // 提取下一關簽核人員的 ID
      const approverList = (nextStep as any).approverList;
      const approverIds = approverList.map((approver: any) => approver.id).filter((id: any) => id);

      if (approverIds.length === 0) {
        console.log('下一關簽核人員 ID 列表為空，不發送通知');
        return;
      }

      console.log('準備發送簽核通知:', {
        nextStepName: nextStep.stepName,
        approverCount: approverIds.length,
        approverIds
      });

      // 取得當前語系並確保是支援的語系
      const currentLocale = this.getCurrentLanguageSync();

      // 取得電子郵件地址
      const emailResponse = await this.getUserEmails(approverIds);
      console.log('電子郵件取得結果:', emailResponse);

      if (emailResponse && emailResponse.success && emailResponse.emails && emailResponse.emails.length > 0) {
        
        // 準備簽核資訊
        const approvalInfo = {
          formName: this.formId || '簽核表單',
          flowName: '簽核流程',
          currentStepName: this.approvalFlow[actualCurrentIndex]?.stepName || '',
          currentStepLevel: actualCurrentIndex + 1,
          nextStepName: nextStep.stepName || '',
          nextStepLevel: nextStepIndex + 1,
          previousApproverName: this.currentUserName || '前一關簽核人',
          previousApprovalResult: '同意', // 固定為同意，因為只有在 approve 時才會呼叫
          taskUrl: this.generateTaskUrl(),
          deadline: undefined, // 可以根據需要添加期限邏輯
          companyId: environment.companyid
        };

        console.log('準備發送郵件，簽核資訊:', approvalInfo);

        // 發送通知郵件
        const emailResult = await this.sendApprovalNotification(emailResponse.emails, approvalInfo, currentLocale);
        console.log('簽核通知郵件發送成功', {
          nextStepIndex,
          nextStepName: nextStep.stepName,
          recipientCount: emailResponse.emails.length,
          locale: currentLocale,
          result: emailResult
        });
      } else {
        console.warn('無法取得下一關簽核人員的電子郵件地址', emailResponse);
      }
    } catch (error) {
      console.error('發送簽核通知失敗:', error);
      // 注意：不要讓郵件發送失敗影響簽核流程
    }
  }

  /**
   * 取得使用者電子郵件地址
   */
  private getUserEmails(userIds: string[]): Promise<any> {
    const companyId = environment.companyid;
    const apiUrl = environment.apiUrl;

    if (!companyId || !apiUrl) {
      console.error('Missing companyId or apiUrl in environment');
      return Promise.resolve({ success: false, emails: [], error: 'Missing environment configuration' });
    }

    if (!userIds || userIds.length === 0) {
      console.error('UserIds array is empty or null');
      return Promise.resolve({ success: false, emails: [], error: 'No user IDs provided' });
    }

    console.log('Fetching emails for user IDs:', userIds);

    return this.http.post(`${apiUrl}/api/users/get-emails-by-ids`, {
      companyid: companyId,
      ids: userIds
    }).toPromise().catch(error => {
      console.error('Error fetching user emails:', error);
      return { success: false, emails: [], error: error.message || 'API call failed' };
    });
  }

  /**
   * 發送簽核通知郵件
   */
  private async sendApprovalNotification(
    recipientEmails: string[],
    approvalInfo: any,
    locale: 'zh-TW' | 'ja-JP' | 'en-US'
  ): Promise<void> {
    try {
      const result = await this.emailService.sendApprovalNotification(
        recipientEmails,
        approvalInfo,
        locale
      ).toPromise();
      
      console.log('簽核通知郵件發送結果:', result);
    } catch (error) {
      console.error('簽核通知郵件發送失敗:', error);
      throw error;
    }
  }

  /**
   * 生成任務連結 URL
   */
  private generateTaskUrl(): string {
    // 根據實際需求生成任務連結
    // 這裡提供一個基本的實作
    const baseUrl = window.location.origin;
    const formPath = '/approval'; // 根據實際路由調整
    const queryParams = this.formId ? `?formId=${this.formId}` : '';
    
    return `${baseUrl}${formPath}${queryParams}`;
  }

  /**
   * 取得當前語言值（同步），並確保是支援的語系
   */
  private getCurrentLanguageSync(): 'zh-TW' | 'en-US' | 'ja-JP' {
    const currentLang = this.i18nService.currentLangValue;
    
    // 確保回傳的語系是郵件服務支援的語系
    switch (currentLang) {
      case 'ja-JP':
        return 'ja-JP';
      case 'en-US':
        return 'en-US';
      case 'zh-TW':
      default:
        return 'zh-TW'; // 預設使用繁體中文
    }
  }
}
